import { unwrapResult } from '@reduxjs/toolkit';
import { slices, useAppDispatch, useAppSelector } from 'src/redux';
import { GetSellOutRequest } from 'src/redux/slices/sell-out';
import { useSnackBar } from './use-snackbar';
import {
  convertStringToCsv,
  dateToday,
  downloadCSV,
  formatDate
} from 'src/utils';

const { actions: sellOutActions, selectors: sellOutSelectors } = slices.sellOut;

const useSellOut = () => {
  const dispatch = useAppDispatch();
  const snackBar = useSnackBar();

  const sellOutList = useAppSelector(sellOutSelectors?.selectSellOutList);
  const sellOutSummary = useAppSelector(sellOutSelectors?.selectSellOutSummary);
  const appliedFilter = useAppSelector(sellOutSelectors?.selectFilter);
  const meta = useAppSelector(sellOutSelectors?.selectMeta);

  const isLoading = useAppSelector(sellOutSelectors?.selectSellOutIsLoading);

  const getSellOutList = async (params?: GetSellOutRequest) => {
    try {
      const response = unwrapResult(
        await dispatch(sellOutActions?.getSellOutListThunk(params || {}))
      );

      if (response?.success) {
        return;
      } else {
        const error_keys = Object.keys(response?.errors || {});
        snackBar?.show({
          message: `Error fetching sell out list: ${error_keys
            .join(', ')
            .replace(/_/g, ' ')} is required`,
          severity: 'error',
          useSound: true
        });
      }
    } catch (error) {
      console.error('Error fetching sell out list:', error);
    }
  };

  const getSellOutSummary = async (params?: GetSellOutRequest) => {
    try {
      const response = unwrapResult(
        await dispatch(sellOutActions?.getSellOutSummaryThunk(params || {}))
      );

      if (response?.success) {
        return;
      } else {
        const error_keys = Object.keys(response?.errors || {});
        snackBar?.show({
          message: `Error fetching sell out list: ${error_keys
            .join(', ')
            .replace(/_/g, ' ')} is required`,
          severity: 'error',
          useSound: true
        });
      }
    } catch (error) {
      console.error('Error fetching sell out summary:', error);
    }
  };

  const getSellOutNvidiaCSV = async (params?: GetSellOutRequest) => {
    try {
      const response = unwrapResult(
        await dispatch(sellOutActions?.getSellOutNvidiaThunk(params || {}))
      );

      const selloutCsvData = response?.originalData?.data?.map((item) => item);
      downloadCSV(
        selloutCsvData,
        `NVIDIA SELLOUT ${formatDate(params?.start_date)} TO ${formatDate(
          params?.end_date
        )}`
      );
    } catch (error) {
      console.error('Error fetching sell out summary:', error);
    }
  };

  const getSellOutCsv = async (params?: GetSellOutRequest) => {
    try {
      const response = unwrapResult(
        await dispatch(sellOutActions?.getSellOutCsvThunk(params || {}))
      );

      const csv_data = convertStringToCsv(response?.originalData);

      downloadCSV(csv_data, `sell-out-${dateToday()}`);
    } catch (error) {
      console.error('Error fetching sell out summary:', error);
    }
  };

  const setFilter = (params: GetSellOutRequest) => {
    dispatch(sellOutActions?.sellOutParams(params));
  };

  return {
    sellOutList,
    sellOutSummary,
    isLoading,
    meta,
    appliedFilter,
    getSellOutList,
    getSellOutSummary,
    getSellOutCsv,
    setFilter,
    getSellOutNvidiaCSV
  };
};

export default useSellOut;
